import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import { toast } from 'react-toastify';

function SupplierLogin() {
  const navigate = useNavigate();
  const { login, register, resetPassword, setupPassword, loading, error, isAuthenticated, clearError } = useSupplierAuth();
  
  const [mode, setMode] = useState('login'); // 'login', 'register', 'reset', 'resetVerify', 'setPassword', 'resetPassword'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    vendor: '',
    pin: '',
    name: '',
    position: ''
  });

  // Redirect if already authenticated
  useEffect(() => {
    // Note: Do NOT remove 'token' from localStorage here
    // The main app uses 'token' for authentication
    // The supplier system uses 'supplier_token' (separate session)
    
    if (isAuthenticated()) {
      navigate('/supplier/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Clear error when component mounts or mode changes
  useEffect(() => {
    clearError();
  }, [mode, clearError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (mode === 'login') {
        const result = await login({
          email: formData.email,
          password: formData.password
        });
        
        if (result.success) {
          toast.success('Login successful!');
          navigate('/supplier/dashboard');
        } else {
          toast.error(result.message);
        }
      } else if (mode === 'register') {
        const result = await register({
          email: formData.email,
          vendor: formData.vendor,
          pin: formData.pin
        });
        
        if (result.status === 1) {
          if (formData.pin) {
            // OTP verification successful for existing supplier registration
            toast.success('Registration complete! You are now logged in.');
            
            // Token and supplier state are already set by the register function in context
            // Just set the password setup flag
            if (result.token) {
              localStorage.setItem('supplier_needs_password_setup', 'true');
            }
            
            // Small delay to ensure context state is updated before navigation
            setTimeout(() => {
              navigate('/supplier/dashboard');
            }, 100);
          } else {
            toast.success('OTP sent to your email. Please check your inbox.');
            setMode('verify');
          }
        }
      } else if (mode === 'verify') {
        // OTP verification for registration
        const result = await register({
          email: formData.email,
          vendor: formData.vendor,
          pin: formData.pin
        });
        
        if (result.status === 1) {
          // OTP verification successful - now prompt for password setup
          toast.success('OTP verified! Please set your password.');
          
          // Token and supplier state are already set by the register function in context
          // Set the password setup flag
          if (result.token) {
            localStorage.setItem('supplier_needs_password_setup', 'true');
          }
          
          // Switch to password setup mode instead of navigating to dashboard
          setMode('setPassword');
        }
      } else if (mode === 'reset') {
        const result = await resetPassword({
          email: formData.email,
          pin: formData.pin
        });
        
        if (result.status === 1) {
          if (formData.pin) {
            // Password reset OTP verification successful
            toast.success('Password reset successful! You are now logged in.');
            
            // Handle authentication for password reset
            if (result.token) {
              localStorage.setItem('supplier_token', result.token);
              localStorage.setItem('supplier_authenticated', 'true');
              // Password reset should definitely require new password setup
              localStorage.setItem('supplier_needs_password_setup', 'true');
            }
            
            navigate('/supplier/dashboard');
          } else {
            toast.success('OTP sent to your email. Please check your inbox.');
            setMode('resetVerify');
          }
        }
      } else if (mode === 'resetVerify') {
        const result = await resetPassword({
          email: formData.email,
          pin: formData.pin
        });
        
        if (result.status === 1) {
          // OTP verification successful, now allow user to set new password
          toast.success('OTP verified! Please set your new password.');
          setMode('resetPassword');
        }
      } else if (mode === 'resetPassword') {
        // Validate password confirmation
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match. Please try again.');
          return;
        }
        
        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters long.');
          return;
        }
        
        // Call API to update the password (without name/position since user already exists)
        const result = await setupPassword({
          email: formData.email,
          password: formData.password
        });
        
        if (result.status === 1) {
          toast.success('Password reset successfully! Please login with your new password.');
          
          // Reset form and switch back to login mode
          resetForm();
          setTimeout(() => {
            setMode('login');
          }, 1500);
        }
      } else if (mode === 'setPassword') {
        // Validate required fields
        if (!formData.name || !formData.position) {
          toast.error('Please provide your name and position.');
          return;
        }
        
        // Validate password confirmation
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match. Please try again.');
          return;
        }
        
        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters long.');
          return;
        }
        
        // Call API to setup the new password with name and position
        const result = await setupPassword({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          position: formData.position
        });
        
        if (result.status === 1) {
          toast.success('Profile created successfully! Please login with your new credentials.');
          
          // Reset form and switch back to login mode
          resetForm();
          setTimeout(() => {
            setMode('login');
          }, 1500); // Give user time to read the message
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'An error occurred');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      vendor: '',
      pin: '',
      name: '',
      position: ''
    });
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    resetForm();
    clearError();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          
          {/* Left Column - Login Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            {/* Header */}
            <div className="text-center mb-6">
              <img 
                src="/images/logo.png" 
                alt="Logo" 
                className="mx-auto h-12 w-auto mb-4" 
              />
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {mode === 'login' && 'Supplier Login'}
                {mode === 'register' && 'Supplier Registration'}
                {mode === 'verify' && 'Verify Registration'}
                {mode === 'reset' && 'Reset Password'}
                {mode === 'resetVerify' && 'Verify Reset'}
                {mode === 'resetPassword' && 'Set New Password'}
                {mode === 'setPassword' && 'Complete Your Profile'}
              </h2>
              <p className="text-gray-600 text-sm">
                {mode === 'login' && 'Access your existing supplier portal account'}
                {mode === 'register' && 'Register for portal access (existing suppliers only)'}
                {mode === 'verify' && 'Enter the OTP sent to your email'}
                {mode === 'reset' && 'Reset your password'}
                {mode === 'resetVerify' && 'Enter the OTP sent to your email'}
                {mode === 'resetPassword' && 'Create a new secure password for your account'}
                {mode === 'setPassword' && 'Set up your name, position and password'}
              </p>
          </div>

          {/* New Supplier Alert Banner - Only for login mode */}
          {mode === 'login' && (
            <div className="mb-4 p-3 bg-gradient-to-r from-green-100 to-emerald-100 border-l-4 border-green-500 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-green-800">
                    <strong>New to Silulumanzi?</strong> If you're not yet a registered supplier, 
                    <Link to="/suppliers/new" className="ml-1 font-semibold text-green-700 hover:text-green-600 underline">
                      start your application here →
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Login Mode */}
            {mode === 'login' && (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    autoComplete="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your email address"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    autoComplete="current-password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your password"
                  />
                </div>
              </>
            )}

            {/* Register Mode */}
            {mode === 'register' && (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    autoComplete="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your email address"
                  />
                </div>
                <div>
                  <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor Number
                  </label>
                  <input
                    type="text"
                    id="vendor"
                    name="vendor"
                    value={formData.vendor}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your vendor number"
                  />
                </div>
              </>
            )}

            {/* Verify Modes */}
            {(mode === 'verify' || mode === 'resetVerify') && (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    autoComplete="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your email address"
                  />
                </div>
                <div>
                  <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
                    OTP Code
                  </label>
                  <input
                    type="text"
                    id="pin"
                    name="pin"
                    value={formData.pin}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-2xl tracking-widest"
                    placeholder="12345"
                    maxLength="5"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Enter the 5-digit code sent to your email
                  </p>
                </div>
              </>
            )}

            {/* Reset Mode */}
            {mode === 'reset' && (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  autoComplete="email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Enter your email address"
                />
              </div>
            )}

            {/* Set Password Mode */}
            {mode === 'setPassword' && (
              <>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    autoComplete="name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                    Position
                  </label>
                  <input
                    type="text"
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    required
                    autoComplete="organization-title"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="e.g. Procurement Manager"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    autoComplete="new-password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your new password"
                    minLength="6"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Password must be at least 6 characters long
                  </p>
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    autoComplete="new-password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Confirm your new password"
                    minLength="6"
                  />
                </div>
              </>
            )}

            {/* Reset Password Mode - Only password fields, no name/position */}
            {mode === 'resetPassword' && (
              <>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    autoComplete="new-password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your new password"
                    minLength="6"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Password must be at least 6 characters long
                  </p>
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    autoComplete="new-password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Confirm your new password"
                    minLength="6"
                  />
                </div>
              </>
            )}

            <div>
              <button 
                type="submit" 
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    {mode === 'login' && 'Sign In'}
                    {mode === 'register' && 'Register'}
                    {mode === 'verify' && 'Verify & Complete Registration'}
                    {mode === 'reset' && 'Send Reset Code'}
                    {mode === 'resetVerify' && 'Verify OTP'}
                    {mode === 'resetPassword' && 'Reset Password'}
                    {mode === 'setPassword' && 'Complete Profile'}
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Navigation Links */}
          <div className="mt-4 space-y-3">
            {mode === 'login' && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <button 
                    type="button" 
                    className="text-blue-600 hover:text-blue-500 font-medium transition-colors"
                    onClick={() => switchMode('reset')}
                  >
                    Forgot password?
                  </button>
                  <button 
                    type="button" 
                    className="bg-green-50 text-green-700 hover:bg-green-100 px-3 py-1 rounded-md font-medium transition-colors"
                    onClick={() => switchMode('register')}
                  >
                    Register here
                  </button>
                </div>
              </>
            )}

            {(mode === 'register' || mode === 'verify') && (
              <div className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <button 
                  type="button" 
                  className="text-blue-600 hover:text-blue-500 font-medium transition-colors"
                  onClick={() => switchMode('login')}
                >
                  Sign in here
                </button>
              </div>
            )}

            {(mode === 'reset' || mode === 'resetVerify') && (
              <div className="text-center text-sm text-gray-600">
                Remember your password?{' '}
                <button 
                  type="button" 
                  className="text-blue-600 hover:text-blue-500 font-medium transition-colors"
                  onClick={() => switchMode('login')}
                >
                  Sign in here
                </button>
              </div>
            )}

            {mode === 'setPassword' && (
              <div className="text-center text-sm text-gray-600">
                <button 
                  type="button" 
                  className="text-blue-600 hover:text-blue-500 font-medium transition-colors"
                  onClick={() => switchMode('login')}
                >
                  ← Back to Login
                </button>
              </div>
            )}
          </div>

          {/* Back to Home Link */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="text-center">
              <Link 
                to="/" 
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Home
              </Link>
            </div>
          </div>
        </div>

        {/* Right Column - Information and Quick Actions */}
        <div className="space-y-6">
           
          {/* Quick Actions Card - Always visible */}
          <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl shadow-xl p-6 text-white">
            <div className="text-center mb-4">
              <h3 className="text-lg font-bold mb-1 text-white">Choose Your Path</h3>
              <p className="text-blue-100 text-sm">Select the option that applies to you</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-center">
                  <div className="mx-auto w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-base mb-2 text-white">New Supplier?</h4>
                  <p className="text-white/90 text-xs mb-3">Complete our application process to become a registered supplier</p>
                  <Link 
                    to="/suppliers/new" 
                    className="inline-flex items-center justify-center w-full px-4 py-2 bg-green-500 hover:bg-green-400 text-white text-sm font-semibold rounded-lg transition-colors shadow-lg"
                  >
                    Apply Now →
                  </Link>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-center">
                  <div className="mx-auto w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-base mb-2 text-white">Existing Supplier?</h4>
                  <p className="text-white/90 text-xs mb-3">Register for portal access using your vendor number and email</p>
                  <button 
                    onClick={() => switchMode('register')}
                    className="inline-flex items-center justify-center w-full px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold rounded-lg transition-colors shadow-lg"
                  >
                    Register Now →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Contact our support team for assistance with registration or portal access.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center text-gray-600">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  support@silulumanzi.co.za
                </div>
                <div className="flex items-center justify-center text-gray-600">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  +27 (0) 11 123 4567
                </div>
              </div>
            </div>
          </div>

          
        </div>

        </div>
      </div>
    </div>
  );
}

export default SupplierLogin;
